/**
 * Pastilles de provenance multi-entités — source de vérité UNIQUE.
 *
 * Deux sens, un seul style (bleu ciel) parce qu'ils disent la même chose à
 * l'utilisateur : « cette ligne n'est pas à vous, vous la consultez sans
 * pouvoir agir dessus ». La flèche, elle, doit être juste :
 *
 * - `<InheritedBadge>` — **↑ Hérité** : l'objet appartient à une entité au-dessus
 *   (siège). C'est le cas des référentiels, questionnaires, règles, formations…
 * - `<FromDescendantBadge>` — **↓ Entité rattachée** : l'objet appartient à une
 *   entité en dessous, visible parce qu'on consolide. C'est le cas des FAITS
 *   (réponses de satisfaction, résultats) : ils ne descendent jamais, ils
 *   remontent. Y afficher « ↑ Hérité » serait factuellement inversé.
 *
 * `fromName` (optionnel) : l'entité d'origine, exposée en infobulle.
 * `motif` (optionnel) : la phrase qui la nomme, quand l'objet ne s'accorde pas avec la clé
 * commune « Héritée de {{name}} » (une ligne, une fiche) : un CHAMP se dit « Hérité de Siège »
 * (`custom-fields/PastilleDuChampHerite`). La pastille, elle, reste la même partout.
 *
 * ## La flèche est une ICÔNE, jamais un caractère posé dans le texte
 *
 * 🔴 Elle était le glyphe `↑` écrit à même le JSX. Trois conséquences, toutes
 * silencieuses :
 *
 * 1. **un lecteur d'écran l'ANNONCE** — « flèche vers le haut Hérité » —, alors
 *    qu'elle ne dit rien de plus que le mot qui la suit : c'est du bruit posé
 *    devant l'information ;
 * 2. elle ne suit ni la taille du texte, ni la couleur, ni le trait des autres
 *    pictogrammes de l'écran : une pastille dont le seul symbole vient d'une
 *    fonte système est la seule chose de la page qui ne se règle nulle part ;
 * 3. elle ne se remplace pas — un thème, une densité, un export en image la
 *    laissent telle quelle.
 *
 * ⚠️ Le glyphe n'a JAMAIS eu le droit de vivre dans la valeur traduite : une
 * flèche enfermée dans `common.inherited` se retrouverait à traduire, donc à
 * inverser un jour. Il était déjà hors de la clé ; il est maintenant hors du
 * texte tout court (`ArrowUp` / `ArrowDown`, `aria-hidden`).
 *
 * ⚠️ **Les deux pastilles changent ENSEMBLE.** Ne convertir que l'héritage
 * laisserait deux pastilles jumelles avec deux mécaniques de flèche : c'est
 * exactement la divergence que ce fichier existe pour empêcher.
 *
 * Renvoi : specs/30_LOG/RSC-012_rattachement_formations.md §12, §18
 */

import { ArrowDown, ArrowUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const CHIP_CLASS =
    'inline-flex items-center gap-0.5 shrink-0 rounded-full border border-sky-200 bg-sky-50 px-1.5 py-0 text-[10px] text-sky-700 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-400'

// La flèche fait la hauteur d'une majuscule du libellé, pas celle d'une icône
// d'action : elle accompagne un mot, elle ne se clique pas.
const FLECHE_CLASS = 'h-3 w-3 shrink-0'

export function InheritedBadge({ fromName, motif: phrase }: { fromName?: string | null; motif?: string }) {
    const { t } = useTranslation()
    const motif = fromName
        ? (phrase ?? t('common.inheritedFrom', 'Héritée de {{name}}', { name: fromName }))
        : undefined
    return (
        <span className={CHIP_CLASS} title={motif}>
            <ArrowUp className={FLECHE_CLASS} aria-hidden />
            {t('common.inherited', 'Hérité')}
            {/* ⚠️ **Le motif était SEULEMENT dans le `title`** — RSC-003 §14
                l'interdit nommément : « un `title` n'est ni annoncé ni
                focalisable, et sur mobile il n'existe tout simplement pas ».
                La pastille disait donc « Hérité » sans jamais dire DE QUI, à
                tout le monde sauf à une souris posée dessus — c'est-à-dire
                sans dire à qui demander la modification.

                Le `title` reste (il sert à la souris) ; le texte `sr-only`
                l'ACCOMPAGNE. Les deux pastilles jumelles changent ensemble :
                n'en traiter qu'une laisserait deux mécaniques d'annonce, la
                divergence même que ce fichier existe pour empêcher. */}
            {motif && <span className="sr-only"> — {motif}</span>}
        </span>
    )
}

export function FromDescendantBadge({ fromName }: { fromName?: string | null }) {
    const { t } = useTranslation()
    const motif = fromName
        ? t('common.fromEntity', 'Relevé par {{name}}', { name: fromName })
        : undefined
    return (
        <span className={CHIP_CLASS} title={motif}>
            <ArrowDown className={FLECHE_CLASS} aria-hidden />
            {t('common.fromDescendant', 'Entité rattachée')}
            {/* La jumelle de `InheritedBadge` — même motif annoncé, même
                raison : un `title` seul ne dit rien au clavier ni au doigt. */}
            {motif && <span className="sr-only"> — {motif}</span>}
        </span>
    )
}

/**
 * Provenance d'une ligne, en UNE pastille au plus.
 *
 * Les deux drapeaux sont exclusifs par construction côté serveur
 * (`read.provenance`) : une ligne vient d'au-dessus, d'en dessous, ou de chez
 * soi — jamais deux à la fois. Ce composant existe pour que la condition ne
 * soit pas recopiée dans chaque table : recopiée, elle finit par diverger, et
 * une flèche inversée dit exactement le contraire de la vérité.
 *
 * Ne rend RIEN pour une ligne propre — c'est le cas de 100 % des lignes d'un
 * organisme mono-entité, qui ne doit pas voir la moindre décoration.
 */
export function ProvenanceBadge({
    isInherited,
    isFromDescendant,
    entityName,
}: {
    isInherited?: boolean
    isFromDescendant?: boolean
    entityName?: string | null
}) {
    if (isInherited) return <InheritedBadge fromName={entityName} />
    if (isFromDescendant) return <FromDescendantBadge fromName={entityName} />
    return null
}

export default InheritedBadge
